import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import "./CsatPage.css";
import user from "../../../utils/api/user";
import { getInterviewResult } from "../../../redux/reducer/incbotFeedbackReducer";
import { toast } from "../../../components/hooks/useToast";

const EMOJI_OPTIONS = [
  { key: "very_dissatisfied", emoji: "😡", label: "Very dissatisfied" },
  { key: "dissatisfied", emoji: "😕", label: "Dissatisfied" },
  { key: "neutral", emoji: "😐", label: "Neutral" },
  { key: "satisfied", emoji: "🙂", label: "Satisfied" },
  { key: "very_satisfied", emoji: "😍", label: "Very satisfied" },
];

const EMOJI_RATING_TO_NUMBER: Record<string, number> = {
  very_dissatisfied: 1,
  dissatisfied: 2,
  neutral: 3,
  satisfied: 4,
  very_satisfied: 5,
};

const INITIAL_ANSWERS = {
  recommendScore: null as number | null,
  recommendFeedback: "",
  serviceRating: null as string | null,
  serviceFeedback: "",
  platformRating: null as string | null,
  platformFeedback: "",
  suggestions: "",
};

function FocusTrap({
  containerRef,
  isActive,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  isActive: boolean;
}) {
  useEffect(() => {
    if (!isActive || !containerRef?.current) return;
    const root = containerRef.current;
    const focusables = root.querySelectorAll(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusables[0] as HTMLElement | undefined;
    const last = focusables[focusables.length - 1] as HTMLElement | undefined;
    if (!first) return;
    first.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    root.addEventListener("keydown", handleKeyDown);
    return () => root.removeEventListener("keydown", handleKeyDown);
  }, [isActive, containerRef]);
  return null;
}

export default function CsatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { interviewId = "" } = useParams();
  /** Interview result from incbot (GET_INTERVIEW) or incscreen (GET_FEEDBACK) API */
  const interviewResult = useSelector(getInterviewResult);
  const isIncscreen = location.state === "incscreen";
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [answers, setAnswersState] = useState(INITIAL_ANSWERS);
  const cardRef = useRef<HTMLDivElement>(null);

  const setAnswer = useCallback((key: keyof typeof INITIAL_ANSWERS, value: any) => {
    setAnswersState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetSurvey = useCallback(() => {
    setStep(1);
    setAnswersState(INITIAL_ANSWERS);
  }, []);

  const nextStep = useCallback(() => {
    setStep((s) => Math.min(s + 1, 4));
  }, []);

  const previousStep = useCallback(() => {
    setStep((s) => Math.max(s - 1, 1));
  }, []);

  const handleClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    resetSurvey();
  }, [resetSurvey]);

  const canGoNextStep1 = answers.recommendScore !== null;
  const canGoNextStep2 = answers.serviceRating != null;
  const canGoNextStep3 = answers.platformRating != null;
  const canGoNextStep4 = (answers.suggestions ?? "").trim() !== "";
  const canGoNext =
    step === 1
      ? canGoNextStep1
      : step === 2
        ? canGoNextStep2
        : step === 3
          ? canGoNextStep3
          : canGoNextStep4;

  const handleSubmitSurvey = useCallback(() => {
    const apiPayload = [
      {
        question: "overallInterviewExperience",
        answer: answers.recommendScore ?? 0,
        comments: answers.recommendFeedback?.trim() ?? "",
      },
      {
        question: "evaluationAndFeedbackQuality",
        answer: EMOJI_RATING_TO_NUMBER[answers.serviceRating ?? ""] ?? 0,
        comments: answers.serviceFeedback?.trim() ?? "",
      },
      {
        question: "schedulingAndCoordination",
        answer: EMOJI_RATING_TO_NUMBER[answers.platformRating ?? ""] ?? 0,
        comments: answers.platformFeedback?.trim() ?? "",
      },
      { question: "comment", answer: answers.suggestions?.trim() ?? "" },
    ];
    const payload = {
      answers: apiPayload,
      productName: location.state,
      interviewId,
    };
    setSubmitting(true);
    user
      .post("/csat/feedback/submit", payload, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        const message =
          response?.data?.message ??
          response?.data?.msg ??
          (typeof response?.data === "string" ? response.data : null) ??
          "Feedback submitted successfully.";
        toast({
          title: "Success",
          description: message,
        });
        resetSurvey();
        handleClose();
      })
      .catch(() => setSubmitting(false))
      .finally(() => setSubmitting(false));
  }, [answers, interviewId, location.state, interviewResult, resetSurvey, handleClose]);

  return (
    <div className="csat-page-wrapper">
      <div
        ref={cardRef}
        className="csatModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="csat-title"
        aria-describedby="csat-subtitle"
      >
        <FocusTrap containerRef={cardRef} isActive={true} />

        <div className="csatBody">
          {step === 1 && (
            <>
              <header className="csatHeader">
                <button
                  type="button"
                  className="csatClose"
                  onClick={handleClose}
                  aria-label="Close survey"
                >
                  ×
                </button>
                <h2 id="csat-title" className="csatTitle">
                  We'd ❤️ your feedback
                </h2>
                <p id="csat-subtitle" className="csatSubtitle">
                  Takes less than 30 seconds.
                </p>
              </header>
              <p className="csatQuestion">
                Were you satisfied with the overall interview experience?
              </p>
              <div className="csatEmojiRow">
                {EMOJI_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`csatEmojiCard ${
                      answers.recommendScore === EMOJI_RATING_TO_NUMBER[opt.key]
                        ? "csatEmojiCardSelected"
                        : ""
                    }`}
                    onClick={() => setAnswer("recommendScore", EMOJI_RATING_TO_NUMBER[opt.key])}
                  >
                    <span className="csatEmoji">{opt.emoji}</span>
                  </button>
                ))}
              </div>
              <div className="csatScaleLabels2">
                <span>Very dissatisfied</span>
                <span aria-hidden="true" />
                <span>Neutral</span>
                <span aria-hidden="true" />
                <span>Very satisfied</span>
              </div>
              <div className="csatTextareaWrapper">
                <textarea
                  className="csatTextarea"
                  placeholder="Write your feedback here..."
                  value={answers.recommendFeedback}
                  onChange={(e) => setAnswer("recommendFeedback", e.target.value.slice(0, 500))}
                  maxLength={500}
                  aria-label="Service feedback (500 characters max)"
                />
                <span
                  className={`csatCharCount ${
                    (answers.recommendFeedback?.length ?? 0) >= 500 ? "csatCharCountLimit" : ""
                  }`}
                  aria-live="polite"
                >
                  {answers.recommendFeedback?.length ?? 0}/500
                </span>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <header className="csatHeader">
                <button
                  type="button"
                  className="csatClose"
                  onClick={handleClose}
                  aria-label="Close survey"
                >
                  ×
                </button>
                <h2 id="csat-title" className="csatTitle">
                  Interviewer Evaluation Quality
                </h2>
                <p id="csat-subtitle" className="csatSubtitle">
                  Takes less than 30 seconds.
                </p>
              </header>
              <p className="csatQuestion">
                Were you satisfied with the interviewer's evaluation and feedback quality?
              </p>
              <div className="csatEmojiRow">
                {EMOJI_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`csatEmojiCard ${answers.serviceRating === opt.key ? "csatEmojiCardSelected" : ""}`}
                    onClick={() => setAnswer("serviceRating", opt.key)}
                  >
                    <span className="csatEmoji">{opt.emoji}</span>
                  </button>
                ))}
              </div>
              <div className="csatScaleLabels2">
                <span>Very dissatisfied</span>
                <span aria-hidden="true" />
                <span>Neutral</span>
                <span aria-hidden="true" />
                <span>Very satisfied</span>
              </div>
              <div className="csatTextareaWrapper">
                <textarea
                  className="csatTextarea"
                  placeholder="Write your feedback here..."
                  value={answers.serviceFeedback}
                  onChange={(e) => setAnswer("serviceFeedback", e.target.value.slice(0, 500))}
                  maxLength={500}
                  aria-label="Service feedback (500 characters max)"
                />
                <span
                  className={`csatCharCount ${(answers.serviceFeedback?.length ?? 0) >= 500 ? "csatCharCountLimit" : ""}`}
                  aria-live="polite"
                >
                  {answers.serviceFeedback?.length ?? 0}/500
                </span>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <header className="csatHeader">
                <button
                  type="button"
                  className="csatClose"
                  onClick={handleClose}
                  aria-label="Close survey"
                >
                  ×
                </button>
                <h2 id="csat-title" className="csatTitle">
                  Scheduling & Coordination
                </h2>
                <p id="csat-subtitle" className="csatSubtitle">
                  Takes less than 30 seconds.
                </p>
              </header>
              <p className="csatQuestion">
                Were you satisfied with the scheduling and coordination for this interview?
              </p>
              <div className="csatEmojiRow">
                {EMOJI_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`csatEmojiCard ${answers.platformRating === opt.key ? "csatEmojiCardSelected" : ""}`}
                    onClick={() => setAnswer("platformRating", opt.key)}
                  >
                    <span className="csatEmoji">{opt.emoji}</span>
                  </button>
                ))}
              </div>
              <div className="csatScaleLabels2">
                <span>Very dissatisfied</span>
                <span aria-hidden="true" />
                <span>Neutral</span>
                <span aria-hidden="true" />
                <span>Very satisfied</span>
              </div>
              <div className="csatTextareaWrapper">
                <textarea
                  className="csatTextarea"
                  placeholder="Write your feedback here..."
                  value={answers.platformFeedback}
                  onChange={(e) => setAnswer("platformFeedback", e.target.value.slice(0, 500))}
                  maxLength={500}
                  aria-label="Platform feedback (500 characters max)"
                />
                <span
                  className={`csatCharCount ${(answers.platformFeedback?.length ?? 0) >= 500 ? "csatCharCountLimit" : ""}`}
                  aria-live="polite"
                >
                  {answers.platformFeedback?.length ?? 0}/500
                </span>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <header className="csatHeader">
                <button
                  type="button"
                  className="csatClose"
                  onClick={handleClose}
                  aria-label="Close survey"
                >
                  ×
                </button>
                <h2 id="csat-title" className="csatTitle">
                  Additional Feedback
                </h2>
                <p id="csat-subtitle" className="csatSubtitle">
                  Takes less than 30 seconds.
                </p>
              </header>
              <p className="csatQuestion">Any other suggestions / recommendations or concerns?</p>
              <div className="csatTextareaWrapper">
                <textarea
                  className="csatTextarea csatTextarea2"
                  placeholder="Write your feedback here..."
                  value={answers.suggestions}
                  onChange={(e) => setAnswer("suggestions", e.target.value.slice(0, 500))}
                  maxLength={500}
                  aria-label="Feedback suggestions (500 characters max)"
                />
                <span
                  className={`csatCharCount ${(answers.suggestions?.length ?? 0) >= 500 ? "csatCharCountLimit" : ""}`}
                  aria-live="polite"
                >
                  {answers.suggestions?.length ?? 0}/500
                </span>
              </div>
            </>
          )}
        </div>

        <footer className="csatFooter">
          {step > 1 && (
            <button
              type="button"
              className="csatBtn csatBtnSecondary"
              onClick={previousStep}
              aria-label="Go back to previous question"
            >
              Back
            </button>
          )}
          <div className="csatFooterPrimary">
            {step < 4 ? (
              <button
                type="button"
                className="csatBtn csatBtnPrimary"
                disabled={!canGoNext}
                onClick={nextStep}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                className="csatBtn csatBtnPrimary"
                disabled={submitting || !canGoNext}
                onClick={handleSubmitSurvey}
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
